import { createFileRoute, notFound } from "@tanstack/react-router";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { PageGallery } from "@/components/PageGallery";
import { pageHeadForProvince, getActiveSiteUrl } from "@/lib/seo-helpers";
import { buildBreadcrumbSchema, buildFaqSchema, buildSpeakableSchema } from "@/lib/schema";
import {
  CONSULAR_DOC_LANDINGS,
  getConsularDocLanding,
  type ConsularDocLanding,
} from "@/content/consular-corpus/documents";

function faqsTh(d: ConsularDocLanding) {
  return [
    { q: `${d.titleTh} มีขั้นตอนอะไรบ้าง?`, a: d.chainTh.join(" → ") },
    { q: "ใช้ยื่นกับหน่วยงานไหนได้บ้าง?", a: d.useCaseTh },
    { q: "ต้องเตรียมเอกสารอะไร?", a: d.prepareTh.join(" · ") },
    { q: "สาเหตุที่ถูกตีกลับบ่อยคืออะไร?", a: d.pitfallsTh.join(" · ") },
    { q: "ใช้เวลาและค่าธรรมเนียมเท่าไหร่?", a: d.turnaroundTh },
    { q: "ใช้ Apostille แทนการรับรองสถานทูตได้แล้วหรือยัง?", a: d.apostilleNoteTh },
    {
      q: "มอบอำนาจให้เอเจนซี่ยื่นนิติกรณ์แทนได้ไหม?",
      a: "ได้ครับ ต้องมีหนังสือมอบอำนาจติดอากรแสตมป์ 10 บาท พร้อมสำเนาบัตรประชาชนของผู้มอบและผู้รับมอบที่ลงนามรับรองสำเนาถูกต้อง หากผู้มอบอำนาจอยู่ต่างประเทศ ให้สถานเอกอัครราชทูตหรือสถานกงสุลไทยรับรองลายมือชื่อก่อนส่งกลับ",
    },
    {
      q: "ยื่นที่ไหนได้บ้าง และต้องจองคิวไหม?",
      a: "ยื่นได้ที่กองสัญชาติและนิติกรณ์ กรมการกงสุล ถนนแจ้งวัฒนะ และสำนักงานสาขาของกรมการกงสุลตามที่ประกาศ รองรับทั้งแบบ walk-in และการนัดหมายล่วงหน้า ช่วง 08:00–11:30 คิวยาวที่สุด ทีมเรารับยื่นแทนแบบ door-to-door จากภูเก็ตและทั่วประเทศ",
    },
    {
      q: "รับรองคำแปลกับนิติกรณ์ต่างกันอย่างไร?",
      a: "การรับรองคำแปลคือการยืนยันว่าคำแปลตรงกับต้นฉบับ ส่วนนิติกรณ์คือการที่กรมการกงสุลรับรองลายมือชื่อและตราประทับของเจ้าหน้าที่ผู้ออกหรือผู้รับรองเอกสาร เพื่อให้เอกสารมีผลใช้ในต่างประเทศ ทั้งสองขั้นแยกกันและมักต้องทำต่อเนื่องกัน",
    },
  ];
}

export const Route = createFileRoute("/consular-legalization/document/$doc")({
  loader: ({ params }: { params: { doc: string } }) => {
    const d = getConsularDocLanding(params.doc);
    if (!d) throw notFound();
    return { d };
  },
  component: ConsularDocPageTH,
  head: ({ params, loaderData }: { params: { doc: string }; loaderData?: { d: ConsularDocLanding } }) => {
    if (!loaderData) {
      return { meta: [{ title: "ไม่พบประเภทเอกสาร" }, { name: "robots", content: "noindex" }] };
    }
    const site = getActiveSiteUrl();
    const { d } = loaderData;
    return {
      ...pageHeadForProvince({
        title: `${d.titleTh} 2026 — ขั้นตอน ค่าธรรมเนียม เอกสาร`,
        description: `${d.titleTh}: ลำดับชั้นการรับรอง เอกสารที่ต้องเตรียม ค่าธรรมเนียม 200/400 บาท สาเหตุที่ถูกตีกลับ และไทม์ไลน์ Apostille 2570`,
        path: `/consular-legalization/document/${params.doc}`,
        type: "article",
        alternates: [{ lang: "en", path: `/en/consular-legalization/document/${params.doc}` }],
        keywords: [d.titleTh, "นิติกรณ์กรมการกงสุล", "รับรองเอกสารกงสุล", "MFA legalization", "Apostille ไทย"],
      }),
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify(
            buildBreadcrumbSchema([
              { name: "หน้าแรก", url: `${site}/` },
              { name: "รับรองเอกสารกงสุล", url: `${site}/consular-legalization` },
              { name: d.titleTh, url: `${site}/consular-legalization/document/${params.doc}` },
            ]),
          ),
        },
        { type: "application/ld+json", children: JSON.stringify(buildFaqSchema(faqsTh(d))) },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "HowTo",
            name: d.titleTh,
            step: d.chainTh.map((s, i) => ({ "@type": "HowToStep", position: i + 1, name: s })),
          }),
        },
        { type: "application/ld+json", children: JSON.stringify(buildSpeakableSchema(`/consular-legalization/document/${params.doc}`)) },
      ],
    };
  },
});

function ConsularDocPageTH() {
  const { d } = Route.useLoaderData();
  const faqs = faqsTh(d);
  return (
    <>
      <SiteHeader />
      <main className="container mx-auto max-w-4xl px-4 py-10">
        <nav aria-label="breadcrumb" className="mb-4 text-sm text-muted-foreground">
          <a href="/" className="hover:underline">หน้าแรก</a> <span>/</span>{" "}
          <a href="/consular-legalization/faq" className="hover:underline">รับรองกงสุล</a> <span>/</span>{" "}
          <span>{d.titleTh}</span>
        </nav>

        <h1 className="mb-3 text-3xl font-bold md:text-4xl">{d.titleTh} (2026)</h1>
        <p className="mb-6 text-lg text-muted-foreground" data-speakable="true">
          เอกสารต้นทาง: <strong>{d.issuerTh}</strong>
        </p>

        <div className="mb-8 flex flex-wrap gap-2 text-sm">
          <a href="/contact" className="rounded-md bg-primary px-4 py-2 font-medium text-primary-foreground">ขอใบเสนอราคาฟรี →</a>
          <a href="/consular-legalization/faq" className="rounded-md border px-4 py-2 font-medium hover:bg-muted">FAQ รับรองกงสุล</a>
          <a href={`/en/consular-legalization/document/${d.slug}`} className="rounded-md border px-4 py-2 text-muted-foreground hover:bg-muted">English →</a>
        </div>

        <section className="mb-10 rounded-lg border bg-card p-6">
          <h2 className="mb-3 text-xl font-bold">ลำดับขั้นการรับรอง</h2>
          <ol className="list-decimal space-y-1 pl-6 text-sm text-muted-foreground">
            {d.chainTh.map((x: string, i: number) => <li key={i}>{x}</li>)}
          </ol>
          <p className="mt-4 text-sm text-muted-foreground"><strong>ระยะเวลาและค่าธรรมเนียม:</strong> {d.turnaroundTh}</p>
        </section>

        <section className="mb-10">
          <h2 className="mb-3 text-xl font-bold">ใช้ทำอะไร / ยื่นกับใคร</h2>
          <p className="text-sm leading-relaxed text-muted-foreground">{d.useCaseTh}</p>
        </section>

        <section className="mb-10">
          <h2 className="mb-3 text-xl font-bold">เตรียมเอกสารก่อนยื่น</h2>
          <ul className="list-disc space-y-1 pl-6 text-sm text-muted-foreground">
            {d.prepareTh.map((x: string, i: number) => <li key={i}>{x}</li>)}
          </ul>
        </section>

        <section className="mb-10">
          <h2 className="mb-3 text-xl font-bold">สาเหตุที่เอกสารถูกตีกลับ</h2>
          <ul className="list-disc space-y-1 pl-6 text-sm text-muted-foreground">
            {d.pitfallsTh.map((x: string, i: number) => <li key={i}>{x}</li>)}
          </ul>
        </section>

        <section className="mb-10 rounded-lg border bg-muted/20 p-6">
          <h2 className="mb-3 text-xl font-bold">ไทม์ไลน์ Apostille กับเอกสารชุดนี้</h2>
          <p className="text-sm leading-relaxed text-muted-foreground">{d.apostilleNoteTh}</p>
        </section>

        <section className="mb-10">
          <h2 className="mb-3 text-xl font-bold">คำถามที่พบบ่อย — {d.titleTh}</h2>
          <div className="space-y-3">
            {faqs.map((f, i) => (
              <details key={i} className="rounded-lg border bg-card p-4" {...(i < 3 ? { open: true } : {})}>
                <summary className="cursor-pointer text-sm font-semibold">{f.q}</summary>
                <p className="faq-answer mt-2 text-sm leading-relaxed text-muted-foreground">{f.a}</p>
              </details>
            ))}
          </div>
        </section>

        <section className="mb-10">
          <h2 className="mb-3 text-xl font-bold">รับรองนิติกรณ์เอกสารประเภทอื่น</h2>
          <div className="flex flex-wrap gap-2 text-xs">
            {CONSULAR_DOC_LANDINGS.filter((x) => x.slug !== d.slug).map((x) => (
              <a key={x.slug} href={`/consular-legalization/document/${x.slug}`} className="rounded-full border bg-muted/40 px-3 py-1 text-muted-foreground hover:bg-muted">
                {x.titleTh}
              </a>
            ))}
          </div>
        </section>

        <p className="mb-6 text-xs text-muted-foreground">
          ข้อมูล ณ สิงหาคม 2569 (2026) — ขั้นตอนและค่าธรรมเนียมนิติกรณ์อ้างอิงกองสัญชาติและนิติกรณ์ กรมการกงสุล
          (consular.mfa.go.th) · สถานะอนุสัญญา Apostille อ้างอิง hcch.net (มีผลกับไทย 28 กุมภาพันธ์ 2570)
          — โปรดตรวจสอบข้อกำหนดกับหน่วยงานผู้รับก่อนดำเนินการ
        </p>
        <div className="mb-10">
          <PageGallery category="consular" seed="consular-document.doc" locale="th" />
        </div>

      </main>
      <SiteFooter />
    </>
  );
}
